import React, { useMemo } from 'react';
import { V3SettlementMath } from '../../V3SettlementMath/V3SettlementMath';

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0.00';
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  return value < 0 ? `(${formatted})` : formatted;
};

const SectionTable = ({ title, section, showActuals }) => {
  if (!section || !section.items || section.items.length === 0) return null;

  const visibleItems = section.items.filter((item) => !item.settlementHidden);
  if (visibleItems.length === 0) return null;

  return (
    <div className="mb-4">
      <h6 className="mb-2 font-weight-bold">{title}</h6>
      <div className="table-responsive">
        <table className="table table-sm table-borderless mb-0">
          <thead>
            <tr className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase' }}>
              <th>Item</th>
              <th className="text-right" style={{ width: '120px' }}>Offer</th>
              {showActuals && (
                <th className="text-right" style={{ width: '120px' }}>Actuals</th>
              )}
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item, index) => (
              <tr key={item.id || index}>
                <td>
                  {item.name}
                  {item.settlementOverridden && (
                    <span className="badge badge-warning ml-1" style={{ fontSize: '9px' }}>
                      Override
                    </span>
                  )}
                  {item.isSettlementLineItem && (
                    <span className="badge badge-info ml-1" style={{ fontSize: '9px' }}>
                      Settlement
                    </span>
                  )}
                </td>
                <td className="text-right">{formatCurrency(item.calculatedTotal)}</td>
                {showActuals && (
                  <td className="text-right">{formatCurrency(item.calculatedActualTotal)}</td>
                )}
              </tr>
            ))}
            <tr className="font-weight-bold" style={{ borderTop: '2px solid #dee2e6' }}>
              <td>Total</td>
              <td className="text-right">{formatCurrency(section.total)}</td>
              {showActuals && (
                <td className="text-right">{formatCurrency(section.actualTotal)}</td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const V3SettlementView = ({ offer, budget, lineItems, settlementLineItems, settlementOverrides, settlementVisibilities }) => {
  const calculated = useMemo(() => {
    if (!offer || !budget) return null;

    const offerWithSettlement = {
      ...offer,
      v3_settlement_line_items: settlementLineItems || [],
      v3_settlement_overrides: settlementOverrides || [],
      v3_settlement_visibilities: settlementVisibilities || [],
    };

    const budgetWithLineItems = {
      ...budget,
      v3_budget_line_items_tickets: (lineItems || []).filter((li) => li.category === 'TICKET'),
      v3_budget_line_items_additions: (lineItems || []).filter((li) => li.category === 'ADDITION'),
      v3_budget_line_items_income: (lineItems || []).filter((li) => li.category === 'INCOME'),
      v3_budget_line_items_expenses: (lineItems || []).filter((li) => li.category === 'EXPENSES'),
      v3_budget_line_items_before_adjusted_deductions: (lineItems || []).filter((li) => li.category === 'BEFORE_ADJUSTED_DEDUCTIONS'),
      v3_budget_line_items_deductions: (lineItems || []).filter((li) => li.category === 'DEDUCTIONS'),
    };

    try {
      const math = new V3SettlementMath(offerWithSettlement, budgetWithLineItems);
      return math.calculateSettlement();
    } catch (e) {
      console.error('Settlement calculation error:', e);
      return null;
    }
  }, [offer, budget, lineItems, settlementLineItems, settlementOverrides, settlementVisibilities]);

  if (!offer || !budget) {
    return (
      <div className="p-4 text-center text-muted">
        No budget data available for settlement.
      </div>
    );
  }

  if (!calculated) {
    return (
      <div className="p-4 text-center text-muted">
        Unable to calculate settlement.
      </div>
    );
  }

  const { showSummary, artistEarnings, ticketsSection, revenueSection, beforeAdjustedDeductionsSection, expensesSection, additionsSection, deductionsSection, depositsSection } = calculated;
  const showActuals = budget.show_actuals_column;

  return (
    <div className="p-3">
      <div className="row mb-4">
        <div className="col-12">
          <h5 className="font-weight-bold mb-3">Settlement</h5>
          <div className="card mb-4">
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 text-center">
                  <div className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase' }}>Net Box Office</div>
                  <div className="font-weight-bold" style={{ fontSize: '18px' }}>
                    {formatCurrency(showSummary?.netBoxOfficeReceipts)}
                  </div>
                </div>
                <div className="col-md-3 text-center">
                  <div className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase' }}>Artist Guarantee</div>
                  <div className="font-weight-bold" style={{ fontSize: '18px' }}>
                    {formatCurrency(showSummary?.artistGuarantee)}
                  </div>
                </div>
                <div className="col-md-3 text-center">
                  <div className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase' }}>Artist %</div>
                  <div className="font-weight-bold" style={{ fontSize: '18px' }}>
                    {formatCurrency(showSummary?.artistPercentage)}
                  </div>
                </div>
                <div className="col-md-3 text-center">
                  <div className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase' }}>Remaining Due</div>
                  <div className="font-weight-bold" style={{ fontSize: '18px' }}>
                    {formatCurrency(artistEarnings?.remainingDue)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SectionTable title="Tickets" section={ticketsSection} showActuals={showActuals} />
      <SectionTable title="Income" section={revenueSection} showActuals={showActuals} />
      <SectionTable title="Pre-Adjusted Deductions" section={beforeAdjustedDeductionsSection} showActuals={showActuals} />
      <SectionTable title="Expenses" section={expensesSection} showActuals={showActuals} />
      <SectionTable title="Additions" section={additionsSection} showActuals={showActuals} />
      <SectionTable title="Deductions" section={deductionsSection} showActuals={showActuals} />

      <div className="mb-4">
        <h6 className="mb-2 font-weight-bold">Artist Earnings</h6>
        <div className="table-responsive">
          <table className="table table-sm table-borderless mb-0">
            <tbody>
              <tr>
                <td>Guarantee + Percentage</td>
                <td className="text-right" style={{ width: '150px' }}>
                  {formatCurrency(artistEarnings?.artistGuaranteeAndOrPercentage)}
                </td>
              </tr>
              {artistEarnings?.totalAdditions > 0 && (
                <tr>
                  <td>+ Additions</td>
                  <td className="text-right">{formatCurrency(artistEarnings.totalAdditions)}</td>
                </tr>
              )}
              {artistEarnings?.totalDeductions > 0 && (
                <tr>
                  <td>- Deductions</td>
                  <td className="text-right">{formatCurrency(artistEarnings.totalDeductions)}</td>
                </tr>
              )}
              {artistEarnings?.taxWithholding > 0 && (
                <tr>
                  <td>- Tax Withholding</td>
                  <td className="text-right">{formatCurrency(artistEarnings.taxWithholding)}</td>
                </tr>
              )}
              <tr className="font-weight-bold" style={{ borderTop: '2px solid #dee2e6' }}>
                <td>Artist Total Due</td>
                <td className="text-right">{formatCurrency(artistEarnings?.artistTotalDue)}</td>
              </tr>
              {artistEarnings?.totalDeposits > 0 && (
                <tr>
                  <td>- Deposits</td>
                  <td className="text-right">{formatCurrency(artistEarnings.totalDeposits)}</td>
                </tr>
              )}
              <tr className="font-weight-bold" style={{ borderTop: '2px solid #dee2e6' }}>
                <td>Remaining Due</td>
                <td className="text-right">{formatCurrency(artistEarnings?.remainingDue)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {depositsSection && depositsSection.items && depositsSection.items.length > 0 && (
        <div className="mb-4">
          <h6 className="mb-2 font-weight-bold">Deposits</h6>
          <div className="table-responsive">
            <table className="table table-sm table-borderless mb-0">
              <thead>
                <tr className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase' }}>
                  <th>Description</th>
                  <th className="text-right" style={{ width: '120px' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {depositsSection.items.map((deposit, index) => (
                  <tr key={deposit.id || index}>
                    <td>{deposit.name || deposit.description || `Deposit ${index + 1}`}</td>
                    <td className="text-right">{formatCurrency(deposit.calculatedAmount)}</td>
                  </tr>
                ))}
                <tr className="font-weight-bold" style={{ borderTop: '2px solid #dee2e6' }}>
                  <td>Total Deposits</td>
                  <td className="text-right">{formatCurrency(depositsSection.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default V3SettlementView;
